import { validate } from 'rut.js';
import { EnrollmentData } from './types';

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidRut = (rut: string): boolean => validate(rut);

export const calculateAge = (birthDate: string | Date): number => {
  if (!birthDate) return 0;

  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }

  return age;
};

export const validateEnrollmentData = (
  editData: EnrollmentData,
): ValidationResult => {
  const errors: ValidationError[] = [];

  if (!editData?.student?.user?.name?.trim()) {
    errors.push({ field: 'student.user.name', message: 'Nombre del alumno es requerido' });
  }
  if (!editData?.student?.user?.lastname?.trim()) {
    errors.push({ field: 'student.user.lastname', message: 'Apellidos del alumno es requerido' });
  }
  if (!editData?.student?.rut?.trim()) {
    errors.push({ field: 'student.rut', message: 'RUT del alumno es requerido' });
  } else if (!isValidRut(editData.student.rut)) {
    errors.push({ field: 'student.rut', message: 'El RUT del alumno no es válido' });
  }
  if (!editData?.student?.birthDate) {
    errors.push({ field: 'student.birthDate', message: 'Fecha de nacimiento del alumno es requerido' });
  }
  if (!editData?.student?.user?.email?.trim()) {
    errors.push({ field: 'student.user.email', message: 'Correo del alumno es requerido' });
  } else if (!isValidEmail(editData.student.user.email)) {
    errors.push({ field: 'student.user.email', message: 'El correo del alumno no es válido' });
  }
  if (!editData?.student?.phoneNumber?.trim()) {
    errors.push({ field: 'student.phoneNumber', message: 'Teléfono del alumno es requerido' });
  }

  if (editData?.student?.birthDate) {
    const age = calculateAge(editData.student.birthDate);
    if (age < 15) {
      errors.push({ field: 'student.birthDate', message: 'El estudiante debe tener al menos 15 años' });
    }
  }

  if (editData?.representative) {
    if (!editData.representative.user?.name?.trim()) {
      errors.push({ field: 'representative.user.name', message: 'Nombre del apoderado es requerido' });
    }
    if (!editData.representative.user?.lastname?.trim()) {
      errors.push({ field: 'representative.user.lastname', message: 'Apellidos del apoderado es requerido' });
    }
    if (!editData.representative.rut?.trim()) {
      errors.push({ field: 'representative.rut', message: 'RUT del apoderado es requerido' });
    } else if (!isValidRut(editData.representative.rut)) {
      errors.push({ field: 'representative.rut', message: 'El RUT del apoderado no es válido' });
    }
    if (!editData.representative.birthDate) {
      errors.push({ field: 'representative.birthDate', message: 'Fecha de nacimiento del apoderado es requerido' });
    }
    if (!editData.representative.user?.email?.trim()) {
      errors.push({ field: 'representative.user.email', message: 'Correo del apoderado es requerido' });
    } else if (!isValidEmail(editData.representative.user.email)) {
      errors.push({ field: 'representative.user.email', message: 'El correo del apoderado no es válido' });
    }
    if (!editData.representative.nationality?.trim()) {
      errors.push({ field: 'representative.nationality', message: 'Nacionalidad del apoderado es requerido' });
    }
    if (!editData.representative.maritalStatus?.trim()) {
      errors.push({ field: 'representative.maritalStatus', message: 'Estado civil del apoderado es requerido' });
    }
    if (!editData.representative.phoneNumber?.trim()) {
      errors.push({ field: 'representative.phoneNumber', message: 'Teléfono del apoderado es requerido' });
    }
    if (!editData.representative.address?.trim()) {
      errors.push({ field: 'representative.address', message: 'Dirección del apoderado es requerido' });
    }
    if (!editData.representative.commune?.trim()) {
      errors.push({ field: 'representative.commune', message: 'Comuna del apoderado es requerido' });
    }

    if (editData.representative.birthDate) {
      const age = calculateAge(editData.representative.birthDate);
      if (age < 18) {
        errors.push({ field: 'representative.birthDate', message: 'El apoderado debe ser mayor de edad (18 años)' });
      }
    }
  }

  if (editData?.sustainer) {
    if (!editData.sustainer.user?.name?.trim()) {
      errors.push({ field: 'sustainer.user.name', message: 'Nombre del sostenedor es requerido' });
    }
    if (!editData.sustainer.user?.lastname?.trim()) {
      errors.push({ field: 'sustainer.user.lastname', message: 'Apellidos del sostenedor es requerido' });
    }
    if (!editData.sustainer.rut?.trim()) {
      errors.push({ field: 'sustainer.rut', message: 'RUT del sostenedor es requerido' });
    } else if (!isValidRut(editData.sustainer.rut)) {
      errors.push({ field: 'sustainer.rut', message: 'El RUT del sostenedor no es válido' });
    }
    if (!editData.sustainer.birthDate) {
      errors.push({ field: 'sustainer.birthDate', message: 'Fecha de nacimiento del sostenedor es requerido' });
    }
    if (!editData.sustainer.user?.email?.trim()) {
      errors.push({ field: 'sustainer.user.email', message: 'Correo del sostenedor es requerido' });
    } else if (!isValidEmail(editData.sustainer.user.email)) {
      errors.push({ field: 'sustainer.user.email', message: 'El correo del sostenedor no es válido' });
    }
    if (!editData.sustainer.nationality?.trim()) {
      errors.push({ field: 'sustainer.nationality', message: 'Nacionalidad del sostenedor es requerido' });
    }
    if (!editData.sustainer.maritalStatus?.trim()) {
      errors.push({ field: 'sustainer.maritalStatus', message: 'Estado civil del sostenedor es requerido' });
    }
    if (!editData.sustainer.phoneNumber?.trim()) {
      errors.push({ field: 'sustainer.phoneNumber', message: 'Teléfono del sostenedor es requerido' });
    }
    if (!editData.sustainer.address?.trim()) {
      errors.push({ field: 'sustainer.address', message: 'Dirección del sostenedor es requerido' });
    }
    if (!editData.sustainer.commune?.trim()) {
      errors.push({ field: 'sustainer.commune', message: 'Comuna del sostenedor es requerido' });
    }

    if (editData.sustainer.birthDate) {
      const age = calculateAge(editData.sustainer.birthDate);
      if (age < 18) {
        errors.push({ field: 'sustainer.birthDate', message: 'El sostenedor debe ser mayor de edad (18 años)' });
      }
    }
  }

  if (!editData?.student?.leadSource?.trim()) {
    errors.push({ field: 'student.leadSource', message: '¿Cómo nos conociste? es requerido' });
  }

  // Validar leadName solo para hermano y profesor
  if ((editData?.student?.leadSource === 'hermano' || editData?.student?.leadSource === 'profesor')
      && !editData?.student?.leadName?.trim()) {
    const label = editData.student.leadSource === 'hermano'
      ? 'Nombre del hermano/a'
      : 'Nombre del profesor/a';
    errors.push({ field: 'student.leadName', message: `${label} es requerido` });
  }

  // Validar ambassadorCode solo para embajador y centro_alumnos
  if ((editData?.student?.leadSource === 'embajador' || editData?.student?.leadSource === 'centro_alumnos')
      && !editData?.student?.ambassadorCode?.trim()) {
    const label = editData.student.leadSource === 'embajador'
      ? 'Código del embajador/a'
      : 'Código del centro de alumnos';
    errors.push({ field: 'student.ambassadorCode', message: `${label} es requerido` });
  }

  if (!editData?.student?.locationId) {
    errors.push({ field: 'student.locationId', message: 'Sede es requerido' });
  }
  if (!editData?.student?.classYear?.trim()) {
    errors.push({ field: 'student.classYear', message: 'Nivel a cursar es requerido' });
  }
  if (editData?.student?.inscriptionYear === null
    || editData?.student?.inscriptionYear === undefined) {
    errors.push({ field: 'student.inscriptionYear', message: 'El año de inscripción es requerido' });
  } else if (Number.isNaN(editData.student.inscriptionYear)) {
    errors.push({ field: 'student.inscriptionYear', message: 'El año de inscripción no es válido' });
  }

  if (!editData?.student?.testAreas || editData.student.testAreas.length === 0) {
    errors.push({ field: 'student.testAreas', message: 'Debe seleccionar al menos un área de interés' });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
